<template>
  <div class="page page-center">
    <div class="container container-tight py-4">
      <div class="text-center mb-4">
        <a href="." class="navbar-brand navbar-brand-autodark">
          <img :src="logoUrl" :style="{ height: logoHeight + 'px' }" alt="" class="logo-image" />
        </a>
      </div>
      <div class="card card-md">
        <div class="card-body">
          <h2 class="h2 text-center mb-4">{{ step === 'credentials' ? __DICT__.loginPrompt : __DICT__.twoFactor.title }}</h2>
          <div v-if="errorMessage" class="alert alert-important alert-danger">{{ errorMessage }}</div>
          <div v-if="setupUrl" class="alert alert-important alert-warning">
            <div class="mb-2">{{ __DICT__.twoFactor.setupRequired }}</div>
            <a :href="setupUrl" target="_blank" rel="noopener" class="btn btn-sm btn-warning">
              {{ __DICT__.twoFactor.openSetup }}
            </a>
          </div>

          <form v-if="step === 'credentials'" @submit.prevent="handleSubmit" autocomplete="off" novalidate>
            <div class="mb-3">
              <label class="form-label">{{ __DICT__.emailAddress }}</label>
              <input type="email" v-model="email" class="form-control" :placeholder="__DICT__.emailPlaceholder" autocomplete="off">
            </div>
            <div class="mb-2">
              <label class="form-label">
                {{ __DICT__.password }}
              </label>
              <div class="input-group input-group-flat">
                <input :type="showPassword ? 'text' : 'password'" v-model="password" class="form-control" :placeholder="__DICT__.passwordPlaceholder" autocomplete="off">
                <span class="input-group-text">
                  <a href="#" class="link-secondary" @click.prevent="togglePassword" :title="__DICT__.showPassword" data-bs-toggle="tooltip">
                    <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" class="icon" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7" /></svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-eye-off" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" /><path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" /><path d="M3 3l18 18" /></svg>
                  </a>
                </span>
              </div>
            </div>
            <div class="form-footer">
              <div v-if="isLoading" class="progress progress-sm mb-2">
                <div class="progress-bar progress-bar-indeterminate"></div>
              </div>
              <button :disabled="isLoading" type="submit" class="btn btn-primary w-100">{{  __DICT__.signIn }}</button>
            </div>
          </form>

          <form v-else @submit.prevent="handleVerify" autocomplete="off" novalidate>
            <p class="text-secondary">{{ promptText }}</p>
            <div v-if="infoMessage" class="alert alert-info">{{ infoMessage }}</div>

            <div v-if="!useRecoveryCode" class="mb-3">
              <label class="form-label">{{ __DICT__.twoFactor.codeLabel }}</label>
              <input
                  ref="codeInput"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  v-model="code"
                  class="form-control code-input"
                  :placeholder="__DICT__.twoFactor.codePlaceholder"
              >
            </div>
            <div v-else class="mb-3">
              <label class="form-label">{{ __DICT__.twoFactor.recoveryLabel }}</label>
              <input
                  type="text"
                  autocomplete="off"
                  v-model="recoveryCode"
                  class="form-control"
                  :placeholder="__DICT__.twoFactor.recoveryPlaceholder"
              >
            </div>

            <div class="form-footer">
              <div v-if="isLoading" class="progress progress-sm mb-2">
                <div class="progress-bar progress-bar-indeterminate"></div>
              </div>
              <button :disabled="isLoading || !canVerify" type="submit" class="btn btn-primary w-100">
                {{ isLoading ? __DICT__.twoFactor.verifying : __DICT__.twoFactor.verify }}
              </button>
            </div>

            <div class="mt-3 d-flex flex-column gap-2">
              <button
                  v-for="channel in sendableChannels"
                  :key="channel"
                  type="button"
                  class="btn btn-outline-secondary w-100"
                  :disabled="isSending"
                  @click="requestCode(channel)"
              >
                {{ channel === 'sms' ? __DICT__.twoFactor.sendBySms : __DICT__.twoFactor.sendByEmail }}
              </button>
              <a href="#" class="text-secondary text-center" @click.prevent="toggleRecoveryCode">
                {{ useRecoveryCode ? __DICT__.twoFactor.useCode : __DICT__.twoFactor.useRecoveryCode }}
              </a>
              <a href="#" class="text-secondary text-center" @click.prevent="backToCredentials">
                {{ __DICT__.twoFactor.backToLogin }}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import brandConfig from '@brand/config.js'
import { api } from '@/services/api'
import { useRouter } from 'vue-router'
import squareLogo from '@brand/assets/square.png'
import { useAuthStore } from '@/stores/authStore'
import { usePushReachabilityStore } from '@/stores/pushReachability.js'
import { useInboxStore } from '@/stores/inbox.js'
const authStore = useAuthStore()

const router = useRouter()
const logoUrl = squareLogo
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const logoHeight = ref(150)
const windowHeight = ref(0)

// Two factor challenge state
const step = ref('credentials')
const challengeId = ref('')
const channels = ref([])
const autoSentChannel = ref(null)
const code = ref('')
const recoveryCode = ref('')
const useRecoveryCode = ref(false)
const isSending = ref(false)
const infoMessage = ref('')
const setupUrl = ref('')
const codeInput = ref(null)

const sendableChannels = computed(() => channels.value.filter(channel => channel === 'email' || channel === 'sms'))

const canVerify = computed(() => useRecoveryCode.value ? recoveryCode.value.trim().length > 0 : code.value.trim().length === 6)

const promptText = computed(() => {
  if (channels.value.includes('app')) return __DICT__.twoFactor.appPrompt
  if (autoSentChannel.value === 'sms') return __DICT__.twoFactor.smsPrompt
  return __DICT__.twoFactor.emailPrompt
})

// A challenge is only good for a few minutes, so any failure that invalidates it
// drops the user back to the credentials step rather than leaving them stuck.
const handleAuthError = (error) => {
  if (error.code === 'two_factor_setup_required') {
    setupUrl.value = error.setupUrl || ''
    errorMessage.value = ''
    step.value = 'credentials'
    return true
  }

  if (error.code === 'challenge_expired') {
    errorMessage.value = __DICT__.twoFactor.expired
    backToCredentials()
    return true
  }

  return false
}

const resetChallenge = () => {
  challengeId.value = ''
  channels.value = []
  autoSentChannel.value = null
  code.value = ''
  recoveryCode.value = ''
  useRecoveryCode.value = false
  infoMessage.value = ''
}

const backToCredentials = () => {
  resetChallenge()
  step.value = 'credentials'
}

const toggleRecoveryCode = () => {
  useRecoveryCode.value = !useRecoveryCode.value
  errorMessage.value = ''
}

const completeLogin = async (response) => {
  if (!response.refreshToken) {
    throw new Error(__DICT__.loginErrors.noRefreshToken)
  }

  authStore.setLoggedIn(true)
  // Re-sync this push device for the staff member now signed in, and fetch their
  // reachability and "require push" (notifications-v2 only). The router guard
  // waits on this same request before the first page.
  usePushReachabilityStore().refresh()
  // Their inbox, bell and app badge (#141, notifications-v2 only)
  useInboxStore().refresh()
  await api.get('/api/resort')
  await api.get('/api/contacts')
  await router.push({ name: 'SelectWeek' })
}

const handleSubmit = async () => {
  isLoading.value = true
  errorMessage.value = ''
  setupUrl.value = ''

  try {
    const response = await api.login(email.value, password.value)

    if (response.twoFactorRequired) {
      challengeId.value = response.challengeId
      channels.value = response.channels || []
      autoSentChannel.value = response.autoSentChannel || null
      step.value = 'twoFactor'
      await nextTick()
      codeInput.value?.focus()
      return
    }

    await completeLogin(response)
  } catch (error) {
    if (!handleAuthError(error)) {
      errorMessage.value = error.message || __DICT__.loginErrors.genericError
    }
    authStore.setLoggedIn(false)
  } finally {
    isLoading.value = false
  }
}

const handleVerify = async () => {
  isLoading.value = true
  errorMessage.value = ''
  infoMessage.value = ''

  try {
    const response = await api.verifyTwoFactor({
      challengeId: challengeId.value,
      code: useRecoveryCode.value ? undefined : code.value.trim(),
      recoveryCode: useRecoveryCode.value ? recoveryCode.value.trim() : undefined
    })

    await completeLogin(response)
  } catch (error) {
    if (!handleAuthError(error)) {
      errorMessage.value = error.message || __DICT__.loginErrors.genericError
    }
    code.value = ''
    authStore.setLoggedIn(false)
  } finally {
    isLoading.value = false
  }
}

const requestCode = async (channel) => {
  isSending.value = true
  errorMessage.value = ''
  infoMessage.value = ''

  try {
    await api.sendTwoFactorCode(challengeId.value, channel)
    autoSentChannel.value = channel
    infoMessage.value = __DICT__.twoFactor.codeSent
  } catch (error) {
    if (!handleAuthError(error)) {
      errorMessage.value = error.message || __DICT__.loginErrors.genericError
    }
  } finally {
    isSending.value = false
  }
}

const togglePassword = () => {
  showPassword.value = !showPassword.value
}

const handleResize = () => {
  if (window.visualViewport) {
    const viewportHeight = window.visualViewport.height
    const vh = viewportHeight
    const cardHeight = document.querySelector('.card').offsetHeight
    const containerPadding = 32 // 4 * 8 (py-4)
    const availableSpace = vh - cardHeight - containerPadding - 50
    windowHeight.value = availableSpace;
    logoHeight.value = Math.max(50, Math.min(150, availableSpace))
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('focusin', handleResize)
  window.addEventListener('focusout', handleResize)
  handleResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('focusin', handleResize)
  window.removeEventListener('focusout', handleResize)
})


</script>

<style scoped>
.logo-image {
  transition: height 0.1s ease-in-out;
}

.code-input {
  font-size: 1rem;
  letter-spacing: 0.5em;
  text-align: center;
}
</style>
